/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { SENTRY_DSN_URL, SENTRY_RELEASE } from '@/configs/sentry.config';
import * as Sentry from '@sentry/react';

// Guard to prevent multiple initializations (idempotent)
let isSentryInitialized = false;
let errorHandler: ((event: ErrorEvent) => void) | null = null;
let rejectionHandler: ((event: PromiseRejectionEvent) => void) | null = null;

/**
 * Initialize Sentry for error monitoring and performance tracking
 * This should be called early in your app initialization
 *
 * This function is idempotent - it can be called multiple times safely
 */
export const initSentry = () => {
  // Prevent multiple initializations to avoid memory leaks from duplicate event listeners
  if (isSentryInitialized) {
    return;
  }

  // Track errors that have been processed by error boundaries to prevent duplicates
  const processedErrors = new Set<string>();

  // Add global handlers to catch unhandled errors and prevent automatic Sentry reporting
  errorHandler = (event: ErrorEvent) => {
    const errorKey = `${event.error?.message}-${event.error?.stack?.slice(0, 100)}`;

    // Check if this error was already processed by an error boundary or API handler
    if (processedErrors.has(errorKey)) {
      // console.log('🔄 Preventing duplicate error report for:', event.error?.message);
      event.preventDefault(); // Prevent default error handling
      return;
    }

    // Skip API errors that should be handled by axios interceptors
    if (
      event.error?.message?.includes('Network Error') ||
      event.error?.message?.includes('Request failed') ||
      event.error?.name?.includes('APIError') ||
      event.filename?.includes('axios')
    ) {
      // console.log('🔄 Skipping API error - should be handled by axios interceptor');
      return;
    }

    // Mark as processed to prevent error boundary from sending it again
    processedErrors.add(errorKey);

    // console.log('🔥 Handling unhandled error:', event.error?.message);

    // Manually capture with proper tagging
    if (event.error) {
      Sentry.captureException(event.error, {
        tags: {
          error_source: 'global_handler',
          error_type: 'unhandled_error',
        },
        contexts: {
          error_details: {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            type: 'global_error',
          },
        },
      });
    }
  };

  rejectionHandler = (event: PromiseRejectionEvent) => {
    const errorKey = `${event.reason?.message}-${event.reason?.stack?.slice(0, 100)}`;

    if (processedErrors.has(errorKey)) {
      // console.log('🔄 Preventing duplicate promise rejection for:', event.reason?.message);
      event.preventDefault();
      return;
    }

    processedErrors.add(errorKey);

    // console.log('🔥 Handling unhandled promise rejection:', event.reason);

    Sentry.captureException(event.reason, {
      tags: {
        error_source: 'global_handler',
        error_type: 'unhandled_promise_rejection',
      },
      contexts: {
        error_details: {
          type: 'promise_rejection',
          reason: event.reason,
        },
      },
    });
  };

  // Register event listeners only once
  window.addEventListener('error', errorHandler);
  window.addEventListener('unhandledrejection', rejectionHandler);

  // Expose the processed errors set so error boundary can use it
  (window as any).__sentryProcessedErrors = processedErrors;

  // Only initialize in production or when explicitly enabled
  if (import.meta.env.PROD || import.meta.env.VITE_ENABLE_SENTRY === 'true') {
    Sentry.init({
      // Replace with your actual Sentry DSN
      dsn: SENTRY_DSN_URL,

      // Set environment
      environment: import.meta.env.MODE,

      // Enable performance monitoring
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          // Set sampling rate for session replay
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],

      // Set sampling rate for error monitoring
      sampleRate: 1.0, // 100% of errors

      // Set sampling rate for performance monitoring
      tracesSampleRate: 0.1, // 10% of transactions

      // Configure beforeSend to filter out certain errors
      beforeSend(event, hint) {
        // console.log(
        //   '📤 Sentry beforeSend - Event:',
        //   event.exception?.values?.[0]?.type,
        //   event.exception?.values?.[0]?.value
        // );
        // console.log('🏷️ Tags:', event.tags);

        // ALWAYS allow API errors - NEVER filter them out
        if (event.tags?.error_source === 'api_handler') {
          // console.log('✅ Allowing API error - never filtered');
          return event;
        }

        // Filter out network errors that are not critical (only for non-API sources)
        if (event.exception) {
          const exception = event.exception.values?.[0];
          if (exception?.type === 'NetworkError' && exception.value?.includes('Failed to fetch')) {
            // console.log('❌ Filtered: Network error');
            return null; // Don't send network errors
          }

          // ONLY allow errors that have our error_source tag
          // This blocks ALL automatic captures from Sentry's default integrations
          if (!event.tags?.error_source) {
            // console.log('❌ Filtered: No error_source tag - likely automatic capture');
            return null;
          }

          // Additional safety: Filter out any Error that doesn't start with ReactError_ or APIError_
          // unless it's from our global handler
          if (
            exception?.type === 'Error' &&
            !exception.value?.startsWith('ReactError_') &&
            !exception.value?.startsWith('APIError_') &&
            event.tags?.error_source !== 'global_handler'
          ) {
            // console.log('❌ Filtered: Generic Error without ReactError_/APIError_ prefix');
            return null;
          }
        }

        // Add custom context
        event.tags = {
          ...event.tags,
          app: 'care-admin-fe',
          version: import.meta.env.VITE_APP_VERSION || '1.0.0',
        };

        // console.log('✅ Allowing Sentry event');
        return event;
      },

      // Configure beforeBreadcrumb to filter out certain breadcrumbs
      beforeBreadcrumb(breadcrumb, hint) {
        // Filter out navigation breadcrumbs to reduce noise
        if (breadcrumb.category === 'navigation') {
          return null;
        }

        return breadcrumb;
      },

      // Enable debug mode in development
      debug: import.meta.env.DEV,

      // Set release version
      release: import.meta.env.VITE_RELEASE_NAME,

      // Configure error boundaries
      attachStacktrace: true,

      // Set maximum breadcrumbs
      maxBreadcrumbs: 50,
    });
  }

  // Mark as initialized to prevent duplicate calls
  isSentryInitialized = true;
};

/**
 * Cleanup function to remove Sentry event listeners
 * This should be called when the app unmounts (typically not needed in SPA)
 * but provided for completeness and testing purposes
 */
export const cleanupSentry = () => {
  if (errorHandler) {
    window.removeEventListener('error', errorHandler);
    errorHandler = null;
  }
  if (rejectionHandler) {
    window.removeEventListener('unhandledrejection', rejectionHandler);
    rejectionHandler = null;
  }
  isSentryInitialized = false;
  delete (window as any).__sentryProcessedErrors;
};

/**
 * Set user context for Sentry
 * Call this after user authentication
 */
export const setSentryUser = (user: { id: string; email?: string; username?: string; role?: string }) => {
  if (import.meta.env.PROD || import.meta.env.VITE_ENABLE_SENTRY === 'true') {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username,
      ip_address: '{{auto}}', // Auto-detect IP
    });

    Sentry.setTag('user_role', user.role);
  }
};

/**
 * Clear user context for Sentry
 * Call this on logout
 */
export const clearSentryUser = () => {
  if (import.meta.env.PROD || import.meta.env.VITE_ENABLE_SENTRY === 'true') {
    Sentry.setUser(null);
  }
};

/**
 * Add custom context to Sentry
 */
export const setSentryContext = (name: string, data: Record<string, any>) => {
  if (import.meta.env.PROD || import.meta.env.VITE_ENABLE_SENTRY === 'true') {
    Sentry.setContext(name, data);
  }
};

/**
 * Add custom tag to Sentry
 */
export const setSentryTag = (key: string, value: string) => {
  if (import.meta.env.PROD || import.meta.env.VITE_ENABLE_SENTRY === 'true') {
    Sentry.setTag(key, value);
  }
};

/**
 * Capture a custom error (primarily for API errors)
 * API errors are NOT deduplicated - we want to track every occurrence
 */
export const captureSentryError = (error: Error, context?: Record<string, any>) => {
  if (import.meta.env.PROD || import.meta.env.VITE_ENABLE_SENTRY === 'true') {
    // Don't add API errors to processedErrors - we want to capture every API failure
    // Only mark in processedErrors if it's going to be caught by global handlers
    const errorKey = `${error.message}-${error.stack?.slice(0, 100)}`;
    const processedErrors = (window as any).__sentryProcessedErrors;

    // Only prevent global handlers from re-capturing, but allow multiple API error reports
    if (processedErrors) {
      processedErrors.add(errorKey);
    }

    Sentry.captureException(error, {
      tags: {
        error_source: 'api_handler',
        error_type: 'api_error',
        // Add timestamp to ensure each API error is unique
        timestamp: Date.now().toString(),
      },
      contexts: {
        custom_error_context: context || {},
      },
      // Add fingerprint to prevent Sentry's built-in deduplication for API errors
      fingerprint: ['api-error', error.message, Date.now().toString()],
    });
  }
};

/**
 * Capture a custom message
 */
export const captureSentryMessage = (message: string, level: Sentry.SeverityLevel = 'info') => {
  if (import.meta.env.PROD || import.meta.env.VITE_ENABLE_SENTRY === 'true') {
    Sentry.captureMessage(message, level);
  }
};

export default Sentry;
